import React, { useState, useEffect } from "react";
import { endPoints } from "../../constant/Environment";
import { deleteData, getAllData } from "../../Utility/API";
import { Button } from "react-bootstrap";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { AiOutlineEdit } from "react-icons/ai";
import { Delete } from "@mui/icons-material";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import CreatePopupOffer from "./CreatePopupOffer";
import SegmentationModal from "./SegmentationModal";
import PopupStatsModal from "./PopupStatsModal";
import TriggerIntentModal from "./TriggerIntentModal";

// Derived display helpers — computed client-side from the offer row.
const rewardText = (o) => {
  if (!o.rewardMode || o.rewardMode === "none" || !o.rewardType) return "No Reward";
  switch (o.rewardType) {
    case "BONUS":
      return `${o.rewardBonus ?? 0} Bonus`;
    case "FREE_SPIN":
      return `Free Spins (${o.rewardSpinCount ?? 0} × ${(o.rewardSpinValue ?? 0) / 100})`;
    case "RAF_BOOST":
      return `RAF Boost (+${o.rewardRafPercent ?? 0}%)`;
    case "WOF":
      return "Wheel of Fortune";
    case "BOOSTER":
      return `Booster (+${o.rewardRafPercent ?? 0}%)`;
    default:
      return o.rewardType;
  }
};

const buyPackageText = (o) =>
  o.rewardMode === "on_purchase" || (o.rewardMode === "none" && o.packageId) ? "Yes" : "No";

const packageText = (o, packages) => {
  let id = o.packageId;
  if (!id && o.ctaLink && o.ctaLink.includes("packageId=")) {
    const match = o.ctaLink.match(/packageId=(\d+)/);
    if (match) id = Number(match[1]);
  }
  if (!id) return "None";
  const pkg = packages.find((p) => Number(p.id) === Number(id));
  return pkg ? `$${pkg.purchase_price}` : `#${id}`;
};

const PopupOffers = () => {
  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [segOpen, setSegOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchOffers = async () => {
    setIsLoading(true);
    try {
      const res = await getAllData(endPoints.api.POPUP_OFFER_ALL);
      if (res?.status === "success") setRows(res?.data || []);
      const pkgs = await getAllData(endPoints.api.GET_ALL_PACKAGE);
      if (pkgs?.status === "success") setPackages(pkgs?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const deleteOffer = (id) => {
    setIsLoading(true);
    deleteData(`${endPoints.api.POPUP_OFFER_DELETE}${id}`)
      .then((response) => {
        if (response?.data?.status === "success") {
          SuccesToast("Popup offer deleted.");
          fetchOffers();
        } else {
          ErrorToast(response?.data?.message);
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setIsLoading(false));
  };

  const onClose = () => {
    setCreateOpen(false);
    fetchOffers();
  };

  return (
    <div className="main">
      <Toaster></Toaster>
      {isLoading && <Loader spinner={true} visible={isLoading} />}
      <div id="Sportbook" style={{ padding: "0px" }}>
        <div className="container-fluid bg-color-sportbook custom-card4">
          <div className="inner-space1">
            <div className="row">
              <div
                className="col-md-12"
                style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #444" }}
              >
                <h3 className="mb-2" style={{ fontFamily: "ArticulatCF-Bold" }}>Popup Offers</h3>
                <div style={{ display: "flex", gap: "8px", height: "fit-content" }}>
                  <Button variant="primary" onClick={() => setTriggerOpen(true)}>Triggers</Button>
                  <Button variant="primary" onClick={() => setStatsOpen(true)}>Stats</Button>
                  <Button variant="primary" onClick={() => setSegOpen(true)}>Segmentation</Button>
                  <Button variant="primary" onClick={() => { setEditData({}); setCreateOpen(true); }}>
                    Add Popup Offer
                  </Button>
                </div>
              </div>
            </div>

            <CreatePopupOffer open={createOpen} onClose={onClose} editData={editData} />
            <SegmentationModal open={segOpen} onClose={() => setSegOpen(false)} offers={rows} />
            <PopupStatsModal open={statsOpen} onClose={() => setStatsOpen(false)} />
            <TriggerIntentModal open={triggerOpen} onClose={() => setTriggerOpen(false)} />

            <div className="container-fluid table-custom">
              <div className="row" style={{ padding: "5px 0px 20px 0px" }}>
                <div className="col-md-12">
                  <TableContainer component={Paper}>
                    <Table className="c_table" size="small" stickyHeader sx={{ minWidth: 500 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">Name</TableCell>
                          <TableCell align="center">Trigger</TableCell>
                          <TableCell align="center">Template</TableCell>
                          <TableCell align="center">Priority</TableCell>
                          <TableCell align="center">Reward</TableCell>
                          <TableCell align="center">Purchase Required</TableCell>
                          <TableCell align="center">Package</TableCell>
                          <TableCell align="center">Max Shows</TableCell>
                          <TableCell align="center">Active</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                            <TableCell align="center">{row.name}</TableCell>
                            <TableCell align="center">{row.triggerEvent}</TableCell>
                            <TableCell align="center">{row.template}</TableCell>
                            <TableCell align="center">{row.priority}</TableCell>
                            <TableCell align="center">{rewardText(row)}</TableCell>
                            <TableCell align="center">{buyPackageText(row)}</TableCell>
                            <TableCell align="center">{packageText(row, packages)}</TableCell>
                            <TableCell align="center">{row.maxPerDay}</TableCell>
                            <TableCell align="center">{row.active ? "Yes" : "No"}</TableCell>
                            <TableCell align="center">
                              <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                                <AiOutlineEdit
                                  size={20}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => { setEditData(row); setCreateOpen(true); }}
                                />
                                <span style={{ cursor: "pointer" }} onClick={() => deleteOffer(row.id)}>
                                  <Delete />
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupOffers;
